import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  listQuotations,
  findQuotationByDate,
  getQuotation,
  listSpecialQuotations,
  getEffectiveQuotation,
  getEffectiveQuotationItems,
  createQuotation,
  createSpecialQuotation,
  updateSpecialQuotationTargets,
  updateQuotation,
  deleteQuotation,
  listQuotationItems,
  listQuotationSubmissions,
  createQuotationItem,
  updateQuotationItem,
  deleteQuotationItem,
  bootstrapQuotation,
  copyIntoQuotation,
  copySpecialIntoQuotation,
  activateQuotationForAllClients,
  deactivateAllQuotationPublications,
  deactivateQuotationForAllClients,
  listAvailablePublications,
  listQuotationAssignmentIds,
  listQuotationAssignments,
  replaceQuotationAssignments,
} from "./api";
import type {
  CreateQuotationPayload,
  CreateSpecialQuotationPayload,
  UpdateQuotationPayload,
  CreateItemPayload,
  UpdateItemPayload,
  BootstrapPayload,
  QuotationAssignmentFilter,
  QuotationAssignmentQuery,
  EnrichedQuotationItem,
  PageResponse,
} from "./types";
import type { QueryClient } from "@tanstack/react-query";

export const QUOTATION_HISTORY_PAGE_SIZE = 10;
type QuotationHistoryParams = { limit?: number; offset?: number };
type EffectiveQuotationParams = {
  date?: string;
  storeId?: string;
  clientId?: string;
};
type EffectiveItemsParams = EffectiveQuotationParams & {
  limit?: number;
  offset?: number;
};
const normalizeQuotationHistoryParams = (
  params: QuotationHistoryParams = {},
) => ({
  limit: params.limit ?? QUOTATION_HISTORY_PAGE_SIZE,
  offset: Math.max(0, params.offset ?? 0),
});
export const quotationKeys = {
  all: ["quotations"] as const,
  lists: () => [...quotationKeys.all, "list"] as const,
  list: (params: Required<QuotationHistoryParams>) =>
    [...quotationKeys.lists(), params] as const,
  detail: (id: string) => [...quotationKeys.all, id] as const,
  specials: (id: string) => [...quotationKeys.detail(id), "specials"] as const,
  availablePublications: () =>
    [...quotationKeys.all, "available-publications"] as const,
  effective: () => [...quotationKeys.all, "effective"] as const,
  effectiveBy: (params: EffectiveQuotationParams) =>
    [...quotationKeys.effective(), params] as const,
  effectiveItems: () => [...quotationKeys.all, "effective-items"] as const,
  effectiveItemsBy: (params: EffectiveItemsParams) =>
    [...quotationKeys.effectiveItems(), params] as const,
  assignmentRoot: (quotationId: string) =>
    [...quotationKeys.detail(quotationId), "assignments"] as const,
  assignments: (quotationId: string, query: QuotationAssignmentQuery) =>
    [...quotationKeys.assignmentRoot(quotationId), query] as const,
  items: (quotationId: string, storeId?: string) =>
    [
      ...quotationKeys.detail(quotationId),
      "items",
      { storeId: storeId ?? null },
    ] as const,
};
const normalizeEffectiveItemsParams = (params: {
  date?: string;
  storeId?: string;
  clientId?: string;
  limit?: number;
  offset?: number;
}) => ({
  ...(params.date ? { date: params.date } : {}),
  ...(params.storeId ? { storeId: params.storeId } : {}),
  ...(params.clientId ? { clientId: params.clientId } : {}),
  limit: params.limit ?? 2000,
  offset: params.offset ?? 0,
});
const quotationItemsKey = (quotationId: string) => [
  ...quotationKeys.detail(quotationId),
  "items",
];
const submissionsKey = (date: string) => ["quotations", "submissions", date];
export function invalidateEffectiveQuotationQueries(
  qc: ReturnType<typeof useQueryClient>,
) {
  qc.invalidateQueries({ queryKey: quotationKeys.effective() });
  qc.invalidateQueries({ queryKey: quotationKeys.effectiveItems() });
}

export async function invalidateQuotationPublicationQueries(
  client: QueryClient,
  quotationId: string,
) {
  await Promise.all([
    client.invalidateQueries({ queryKey: quotationKeys.lists() }),
    client.invalidateQueries({
      queryKey: quotationKeys.availablePublications(),
    }),
    client.invalidateQueries({ queryKey: quotationKeys.effective() }),
    client.invalidateQueries({ queryKey: quotationKeys.effectiveItems() }),
    client.invalidateQueries({
      queryKey: quotationKeys.assignmentRoot(quotationId),
    }),
    client.invalidateQueries({
      queryKey: quotationKeys.detail(quotationId),
      exact: true,
    }),
  ]);
}

export function useQuotations(params: QuotationHistoryParams = {}) {
  const normalizedParams = normalizeQuotationHistoryParams(params);

  return useQuery({
    queryKey: quotationKeys.list(normalizedParams),
    queryFn: () =>
      listQuotations(normalizedParams.limit, normalizedParams.offset),
    placeholderData: keepPreviousData,
  });
}

export function useFindQuotationByDate() {
  return useMutation({
    mutationFn: ({ date, excludeId }: { date: string; excludeId?: string }) =>
      findQuotationByDate(date, excludeId),
  });
}

export function useQuotation(id: string) {
  return useQuery({
    queryKey: quotationKeys.detail(id),
    queryFn: () => getQuotation(id),
    enabled: !!id,
  });
}

export function useSpecialQuotations(parentId: string, enabled = true) {
  return useQuery({
    queryKey: quotationKeys.specials(parentId),
    queryFn: () => listSpecialQuotations(parentId),
    enabled: enabled && !!parentId,
  });
}

export function useEffectiveQuotation(
  params: {
    date?: string;
    storeId?: string;
    clientId?: string;
  },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: quotationKeys.effectiveBy(params),
    queryFn: () => getEffectiveQuotation(params),
    enabled:
      (options.enabled ?? true) &&
      Boolean(params.date || params.storeId || params.clientId),
  });
}

export function useEffectiveQuotationItems(
  params: {
    date?: string;
    storeId?: string;
    clientId?: string;
    limit?: number;
    offset?: number;
  },
  options: { enabled?: boolean } = {},
) {
  const normalizedParams = normalizeEffectiveItemsParams(params);

  return useQuery({
    queryKey: quotationKeys.effectiveItemsBy(normalizedParams),
    queryFn: () => getEffectiveQuotationItems(normalizedParams),
    enabled:
      (options.enabled ?? true) &&
      Boolean(params.date || params.storeId || params.clientId),
    placeholderData: keepPreviousData,
  });
}

export function useAvailableQuotationPublications() {
  return useQuery({
    queryKey: quotationKeys.availablePublications(),
    queryFn: ({ signal }) => listAvailablePublications(signal),
  });
}

export function useQuotationAssignments(
  quotationId: string,
  query: QuotationAssignmentQuery,
  enabled = true,
) {
  const normalized = {
    ...query,
    offset: Math.max(0, query.offset),
    search: query.search.trim(),
  };
  return useQuery({
    queryKey: quotationKeys.assignments(quotationId, normalized),
    queryFn: ({ signal }) =>
      listQuotationAssignments(quotationId, normalized, signal),
    enabled: enabled && Boolean(quotationId),
    placeholderData: keepPreviousData,
  });
}

export function useQuotationAssignmentIds() {
  return useMutation({
    mutationFn: ({
      quotationId,
      search,
      assignment,
    }: {
      quotationId: string;
      search: string;
      assignment: QuotationAssignmentFilter;
    }) => listQuotationAssignmentIds(quotationId, { search, assignment }),
  });
}

export function useReplaceQuotationAssignments(quotationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientIds: string[]) =>
      replaceQuotationAssignments(quotationId, clientIds),
    onSuccess: () => invalidateQuotationPublicationQueries(qc, quotationId),
  });
}

export function useActivateQuotationForAllClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quotationId: string) =>
      activateQuotationForAllClients(quotationId),
    onSuccess: (_, quotationId) =>
      invalidateQuotationPublicationQueries(qc, quotationId),
  });
}

export function useDeactivateQuotationForAllClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quotationId: string) =>
      deactivateQuotationForAllClients(quotationId),
    onSuccess: (_, quotationId) =>
      invalidateQuotationPublicationQueries(qc, quotationId),
  });
}

export function useDeactivateAllQuotationPublications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deactivateAllQuotationPublications,
    onSuccess: () => qc.invalidateQueries({ queryKey: quotationKeys.all }),
  });
}

export function useQuotationItems(quotationId: string, storeId?: string) {
  return useQuery({
    queryKey: quotationKeys.items(quotationId, storeId),
    queryFn: () => listQuotationItems(quotationId, 2000, 0, storeId),
    enabled: !!quotationId,
  });
}

export function useQuotationSubmissions(date: string) {
  return useQuery({
    queryKey: submissionsKey(date),
    queryFn: () => listQuotationSubmissions(date, 2000, 0),
    enabled: !!date,
  });
}

export function useCreateQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQuotationPayload) => createQuotation(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: quotationKeys.all }),
  });
}

export function useCreateSpecialQuotation(parentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSpecialQuotationPayload) =>
      createSpecialQuotation(parentId, payload),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: quotationKeys.specials(parentId) });
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      invalidateEffectiveQuotationQueries(qc);
      if (created?.id) {
        qc.invalidateQueries({ queryKey: quotationKeys.detail(created.id) });
        qc.invalidateQueries({ queryKey: quotationItemsKey(created.id) });
      }
    },
  });
}

export function useUpdateSpecialTargets(id: string, parentId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSpecialQuotationPayload) =>
      updateSpecialQuotationTargets(id, payload),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      invalidateEffectiveQuotationQueries(qc);
      if (parentId) {
        qc.invalidateQueries({ queryKey: quotationKeys.specials(parentId) });
      }
      if (updated?.id) {
        qc.invalidateQueries({ queryKey: quotationKeys.detail(updated.id) });
      }
    },
  });
}

export function useUpdateQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateQuotationPayload;
    }) => updateQuotation(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      qc.invalidateQueries({ queryKey: quotationKeys.detail(id) });
    },
  });
}

export function useDeleteQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteQuotation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      invalidateEffectiveQuotationQueries(qc);
    },
  });
}

export function useDeleteSpecialQuotation(parentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteQuotation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      qc.invalidateQueries({ queryKey: quotationKeys.specials(parentId) });
      invalidateEffectiveQuotationQueries(qc);
    },
  });
}

export function useCreateQuotationItem(quotationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateItemPayload) =>
      createQuotationItem(quotationId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quotationItemsKey(quotationId) });
      qc.invalidateQueries({ queryKey: quotationKeys.detail(quotationId) });
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      invalidateEffectiveQuotationQueries(qc);
    },
  });
}

export function useUpdateQuotationItem(quotationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      payload,
    }: {
      itemId: string;
      payload: UpdateItemPayload;
    }) => updateQuotationItem(quotationId, itemId, payload),
    onSuccess: async (updatedItem) => {
      qc.setQueriesData<PageResponse<EnrichedQuotationItem>>(
        { queryKey: quotationItemsKey(quotationId) },
        (current) =>
          current
            ? {
                ...current,
                items: current.items.map((item) =>
                  item.id === updatedItem.id
                    ? { ...item, ...updatedItem }
                    : item,
                ),
              }
            : current,
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: quotationKeys.all }),
        qc.invalidateQueries({ queryKey: ["orders"] }),
        qc.invalidateQueries({ queryKey: ["checkins"] }),
      ]);
    },
  });
}

export function useDeleteQuotationItem(quotationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => deleteQuotationItem(quotationId, itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: quotationItemsKey(quotationId) });
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      invalidateEffectiveQuotationQueries(qc);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useBootstrapQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      payload,
    }: {
      sourceId: string;
      payload: BootstrapPayload;
    }) => bootstrapQuotation(sourceId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: quotationKeys.all }),
  });
}

export function useCopyInto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      targetId,
    }: {
      sourceId: string;
      targetId: string;
    }) => copyIntoQuotation(sourceId, targetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: quotationKeys.all }),
  });
}

export function useCopySpecialInto(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      specialId,
      targetId,
    }: {
      specialId: string;
      targetId: string;
    }) => copySpecialIntoQuotation(sourceId, specialId, targetId),
    onSuccess: (_, { targetId }) => {
      qc.invalidateQueries({ queryKey: quotationKeys.all });
      qc.invalidateQueries({ queryKey: quotationKeys.specials(sourceId) });
      qc.invalidateQueries({ queryKey: quotationKeys.specials(targetId) });
      invalidateEffectiveQuotationQueries(qc);
    },
  });
}
